import {
  EncounterSumaryEncounterStatusEnum,
  EncounterSumaryFormTypeEnum,
  PatientVital,
  PhysicalROSTemplateTemplateTypeEnum,
} from "../../../../../../services";
import { HomeworkTask } from "../../../patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/IndividualTherapyForm/IndividualTherapyForm";

export const STEPS: {
  value: EncounterSumaryEncounterStatusEnum | "checkIn";
  label: string;
}[] = [
  { value: "checkIn", label: "Check In" },
  { value: "INTAKE", label: "Intake" },
  { value: "EXAM", label: "Exam" },
  { value: "CHECKOUT", label: "Sign-Off & Checkout" },
];

type Code = {
  value: string;
  code: string;
};

export type TreatmentPlan = {
  treatment?: string;
  futureConsiderations?: string;
  labStudy?: string;
  referrals?: string;
  collateral?: string;
  safety?: string;
};

export type PsychiatricProgressStateExam = {
  appearance?: string;
  behavior?: string;
  speech?: string;
  mood?: string;
  affect?: string;
  thoughtProcess?: string;
  perceptions?: string;
  cognition?: string;
};

type PsychiatricProgressExam = {
  therapy?: string;
  insightJudgment?: string;
} & PsychiatricProgressStateExam &
  TreatmentPlan;

type TreatMentPlanNote = {
  treatmentGoal?: string;
  estimateCompletionOfTreatmentGoal?: string;
  treatmentStrategy?: string;
  treatmentStrategyEstimatedCompletion?: string;
  frequencyOfTreatment?: string;
  estimateCompletionOfTreatmentStrategy?: string;
  agreedToTreatmentPlan?: boolean;
};

type PreOperativeNote = {
  dateOfProcedure?: string;
  nameOfConsultant?: string;
  attendingPhysician?: string;
  historyOfPresentIllness?: string;
};

type InitialPsychotherapy = {
  safetyPlanCompleted?: any;
  patientIdVerificationMethod?: any;
  safetyAssessmentSuicideRisk?: any;
  studiesScansLabworkReview?: string;
  subjectiveChiefComplaint?: string;

  agreedToPsychiatricEvolutionplanOne?: boolean;
  agreedToPsychiatricEvolutionplanTwo?: boolean;
  agreedToPsychiatricEvolutionplanThree?: boolean;
  agreedToPsychiatricEvolutionplanFour?: boolean;
  psychiatricEvolutionPlanReview?: string;

  mentalHealthScreeningDate?: string;
  interimDate?: string;
  updateWeightDate?: string;

  agreedToClinicalInstructionsplanOne?: boolean;
  agreedToClinicalInstructionsplanTwo?: boolean;
  agreedToClinicalInstructionsplanThree?: boolean;
  agreedToClinicalInstructionsplanFour?: boolean;
  agreedToClinicalInstructionsplanFive?: boolean;
  clinicalInstructionsReview?: string;

  agreedToAdjunctTherapyRecommendationsOne?: boolean;
  agreedToAdjunctTherapyRecommendationsTwo?: boolean;
  agreedToAdjunctTherapyRecommendationsThree?: boolean;
  agreedToAdjunctTherapyRecommendationsFour?: boolean;
  agreedToAdjunctTherapyRecommendationsFive?: boolean;
  agreedToAdjunctTherapyRecommendationsSix?: boolean;
  agreedToAdjunctTherapyRecommendationsSeven?: boolean;
  agreedToAdjunctTherapyRecommendationsEight?: boolean;

  individualTreatmentTherapyRequirements?: string;
  patientGoals?: string;
  weightLossTherapiesOption?: string;
  weightLossTherapiesDescription?: string;
  weightLossTherapiesDuration?: string;
  weightLossTherapiesDurationDescription?: string;

  form504IEP?: boolean;
  parentCARS2Screening?: boolean;
  medAgreementPeds?: boolean;
  controlledSubstanceConsent?: boolean;
  stimulantAgreement?: boolean;
  schoolMedAuth?: boolean;
  loaForm?: boolean;
  esa?: boolean;
  labOrder?: boolean;
  insuranceUpdate?: boolean;
  weightLossContract?: boolean;
  naFormProvided?: boolean;
  noteForSendPatientForms?: string;
  otherFormsProvided?: string;

  accommodationsAtWork?: boolean;
  accommodationsAtSchool?: boolean;
  other?: boolean;

  otherRequestsNote?: string;
};

// Initial Psychiatric Evolution note payload.
// Collected from the Psychiatric Evolution (history), Mental Status Examination
// (MSE) and Risk Assessment modals. Each checkbox section is stored as a
// comma-separated string of the selected option names plus a free-text note.
type InitialPsychiatricEvolution = {
  // Psychiatric history
  pastPsychodynamicHistory?: string;
  pastPsychodynamicHistoryNote?: string;
  psychMedicalHistory?: string;
  psychMedicalHistoryNote?: string;
  substanceUseHistory?: string;
  substanceUseHistoryNote?: string;
  familyPsychiatricHistory?: string;
  familyPsychiatricHistoryNote?: string;
  socialHistory?: string;
  socialHistoryNote?: string;
  // Mental Status Examination (MSE)
  appearance?: string;
  appearanceNote?: string;
  behavior?: string;
  behaviorNote?: string;
  speech?: string;
  speechNote?: string;
  cooperativeness?: string;
  cooperativenessNote?: string;
  mood?: string;
  moodAffectNote?: string;
  thoughtProcess?: string;
  thoughtProcessNote?: string;
  intellectualFunctioning?: string;
  intellectualFunctioningNote?: string;
  thoughtContent?: string;
  thoughtContentNote?: string;
  perceptions?: string;
  perceptionNote?: string;
  cognition?: string;
  cognitionNote?: string;
  insight?: string;
  insightNote?: string;
  judgment?: string;
  judgmentNote?: string;
  overallMseNote?: string;
  // Risk assessment
  riskLevel?: string;
  riskLevelNote?: string;
};

type TraumaFocusedTherapyFormValues = {
  // Trauma & Safety Assessment
  triggersIdentified?: string;
  dissociativeAssessment?: string;
  groundingTechniquesUsed?: string;
  windowOfToleranceAssessment?: string;

  // EMDR Protocol
  emdrPhase?: string;
  targetMemoryImage?: string;
  negativeCognitionNc?: string;
  positiveCognitionPc?: string;
  vocValidityOfCognition?: string;
  emotions?: string;
  sudScale?: string;
  bodySensationsLocations?: string;
  desensitizationSetsProcessing?: string;
  installationProcess?: string;
  bodyScanResults?: string;
  closure?: string;

  // Trauma Processing Work
  traumaNarrativeWork?: string;
  cognitiveProcessingOfTrauma?: string;
  somaticBodyBasedInterventions?: string;

  // Resource Development & Stabilization
  resourceInstallation?: string;
  copingSkillsPracticed?: string;
  stabilizationWork?: string;
  safetyPlanning?: string;

  // Client Response & Progress
  progressTowardTraumaTreatmentGoals?: string;

  // Clinical Impression (with 's' as per API)
  clinicalImpressions?: string;

  // Psychoeducation
  psychoeducationSessionRequired?: boolean;
};

type SolutionFocusedTherapyFormValues = {
  // Future-Focused & Miracle Question
  preferredFutureGoals?: string;
  miracleQuestionResponse?: string;

  // Exceptions & Existing Solutions
  exceptionsIdentified?: string;
  copingQuestions?: string;
  whatsAlreadyWorking?: string;

  // Scaling Questions
  scalingQuestionUsed?: string;
  currentRating?: string;
  previousSessionRating?: string;
  indicatorsOfProgress?: string;

  // Strengths, Resources & Competencies
  clientStrengthsAndResources?: string;
  complimentsOffered?: string;

  // Small Changes & Action Steps
  smallChangesNoticed?: string;
  betweenSessionTasks?: string;
  nextStepsActions?: string;

  // Session Goals & Achievement
  goalsSetForThisSession?: string;
  progressTowardSessionGoals?: string;

  // Client Response & Progress
  clientResponseToSession?: string;
};

type PsychodynamicTherapyFormValues = {
  // Session Information
  sessionDate?: string | null;
  duration?: string;

  // Presenting Material & Session Focus
  materialBroughtToSession?: string;
  sessionFocus?: string;

  // Clinical Assessment - Mental Status Examination
  primaryModality?: string;
  behavior?: string;
  speech?: string;
  mood?: string;
  affect?: string;
  affectRange?: string;
  thoughtProcess?: string;
  thoughtContent?: string;
  cognition?: string;
  insight?: string;
  insightJudgment?: string;

  // Free Association & Dream Work
  freeAssociations?: string;
  dreamContent?: string;
  dreamAnalysis?: string;

  // Transference & Counter Transference
  transferenceObservations?: string;
  counterTransferenceReflections?: string;

  // Resistance & Defense Mechanisms
  defenseMechanismObserved?: string;

  // Unconscious Material & Patterns
  unconsciousConflictsExplored?: string;
  earlyChildhoodExperiences?: string;
  relationalPatterns?: string;

  // Interpretations & Working Through
  interpretationsOffered?: string;
  clientResponseToInterpretations?: string;
  walkingThroughProcess?: string;

  // Insights & Therapeutic Process
  insightsDeveloped?: string;
  therapeuticRelationship?: string;
  progressTowardTreatmentGoal?: string;

  // Clinical Impression & Plan
  clinicalImpression?: string;
  assessmentCodes?: Code[];

  // Plan for Next Session & Risk Assessment
  planForNextSession?: string;
  riskAssessment?: string;
  suicideRisk?: string;
  homicideRisk?: string;

  // Billing Information
  timeSpend?: string;
  documentationTime?: string;
  billable?: boolean;
  procedureCodes?: Code[];
};
// CBT visit note payload (nested under Form2Payload.psychotherapyCbtVisitNote).
// Each intervention has a boolean flag + a `${key}Note` free-text note.
type PsychotherapyCbtVisitNote = {
  cbtSpecificComponents?: string;
  homeworkSection?: string;

  psychoeducationRegardingCbtModel?: boolean;
  psychoeducationRegardingCbtModelNote?: string;
  identificationOfAutomaticThoughts?: boolean;
  identificationOfAutomaticThoughtsNote?: string;
  identificationOfCognitiveDistortions?: boolean;
  identificationOfCognitiveDistortionsNote?: string;
  cognitiveRestructuring?: boolean;
  cognitiveRestructuringNote?: string;
  examinationOfEvidence?: boolean;
  examinationOfEvidenceNote?: string;
  developmentOfAlternativeBalancedThoughts?: boolean;
  developmentOfAlternativeBalancedThoughtsNote?: string;
  behavioralActivation?: boolean;
  behavioralActivationNote?: string;
  problemSolvingSkillsTraining?: boolean;
  problemSolvingSkillsTrainingNote?: string;
  copingSkillsDevelopment?: boolean;
  copingSkillsDevelopmentNote?: string;
  stressManagementTechniques?: boolean;
  stressManagementTechniquesNote?: string;
  relaxationAndGroundingExercises?: boolean;
  relaxationAndGroundingExercisesNote?: string;
  goalSettingAndBehavioralExperiments?: boolean;
  goalSettingAndBehavioralExperimentsNote?: string;
  relapsePreventionPlanning?: boolean;
  relapsePreventionPlanningNote?: string;

  cognitiveDistortionsIdentified?: boolean;
  cognitiveDistortionsIdentifiedNote?: string;

  // Cognitive Distortions Identified (checkbox + note per distortion)
  allOrNothingThinking?: boolean;
  allOrNothingThinkingNote?: string;
  catastrophizing?: boolean;
  catastrophizingNote?: string;
  overgeneralization?: boolean;
  overgeneralizationNote?: string;
  mentalFiltering?: boolean;
  mentalFilteringNote?: string;
  personalization?: boolean;
  personalizationNote?: string;
  mindReading?: boolean;
  mindReadingNote?: string;
  fortuneTelling?: boolean;
  fortuneTellingNote?: string;
  emotionalReasoning?: boolean;
  emotionalReasoningNote?: string;
  labeling?: boolean;
  labelingNote?: string;
  shouldStatements?: boolean;
  shouldStatementsNote?: string;
};

// DBT visit note payload (nested under Form2Payload.psychotherapyDbtVisitNote).
// Each option is a boolean flag; each section has a `${section}ClientResponse`.
type PsychotherapyDbtVisitNote = {
  // Section Client Response text (+ optional note)
  mindfulnessComponent?: string;
  mindfulnessComponentNote?: string;
  emotionRegulation?: string;
  emotionRegulationNote?: string;
  distressTolerance?: string;
  distressToleranceNote?: string;
  interpersonalEffectiveness?: string;
  interpersonalEffectivenessNote?: string;
  behavioralAnalysis?: string;
  behavioralAnalysisNote?: string;

  // Mindfulness options
  mindfulnessObserve?: boolean;
  mindfulnessDescribe?: boolean;
  mindfulnessParticipate?: boolean;
  mindfulnessNonJudgmentally?: boolean;
  mindfulnessOneMindfully?: boolean;
  mindfulnessEffectiveness?: boolean;

  // Emotion Regulation options
  emotionRegulationEmotionIdentification?: boolean;
  emotionRegulationIdentifyingVulnerabilityFactors?: boolean;
  emotionRegulationCheckTheFacts?: boolean;
  emotionRegulationOppositeAction?: boolean;
  emotionRegulationPleaseSkills?: boolean;
  emotionRegulationBuildingPositiveExperiences?: boolean;
  emotionRegulationReducingEmotionalVulnerability?: boolean;
  emotionRegulationManagingEmotionalIntensity?: boolean;

  // Distress Tolerance options
  distressToleranceStopSkill?: boolean;
  distressToleranceTippSkills?: boolean;
  distressToleranceAcceptsSkills?: boolean;
  distressToleranceSelfSoothing?: boolean;
  distressToleranceImproveTheMoment?: boolean;
  distressToleranceProsAndCons?: boolean;
  distressToleranceRadicalAcceptance?: boolean;
  distressToleranceWillingnessVsWillfulness?: boolean;

  // Interpersonal Effectiveness options
  interpersonalEffectivenessDearMan?: boolean;
  interpersonalEffectivenessGive?: boolean;
  interpersonalEffectivenessFast?: boolean;
  interpersonalEffectivenessBoundarySetting?: boolean;
  interpersonalEffectivenessAssertivenessTraining?: boolean;
  interpersonalEffectivenessValidationStrategies?: boolean;
  interpersonalEffectivenessConflictResolution?: boolean;
  interpersonalEffectivenessRelationshipEffectiveness?: boolean;

  // Behavioral Analysis options
  behavioralAnalysisChainAnalysis?: boolean;
  behavioralAnalysisSolutionAnalysis?: boolean;
  behavioralAnalysisDiaryCardReview?: boolean;
  behavioralAnalysisTargetBehaviorReview?: boolean;
  behavioralAnalysisBehavioralRehearsal?: boolean;
  behavioralAnalysisSkillsCoaching?: boolean;

  // Target Behaviors (single select) + Progress (single select)
  targetBehaviors?: string;
  initialProgress?: string;
};

// ACT visit note payload (nested under Form2Payload.psychotherapyActVisitNote).
// Each intervention has a boolean flag + a `${key}Note` Client Response.
type PsychotherapyActVisitNote = {
  psychoeducationRegardingModel?: boolean;
  psychoeducationRegardingModelNote?: string;
  mindfulnessPresentMomentAwareness?: boolean;
  mindfulnessPresentMomentAwarenessNote?: string;
  cognitiveDefusionTechniques?: boolean;
  cognitiveDefusionTechniquesNote?: string;
  acceptanceOfDifficultThoughts?: boolean;
  acceptanceOfDifficultThoughtsNote?: string;
  selfAsContextExercises?: boolean;
  selfAsContextExercisesNote?: string;
  valuesClarification?: boolean;
  valuesClarificationNote?: string;
  committedActionPlanning?: boolean;
  committedActionPlanningNote?: string;
  experientialExercises?: boolean;
  experientialExercisesNote?: string;
  metaphors?: boolean;
  metaphorsNote?: string;
  behavioralCommitmentStrategies?: boolean;
  behavioralCommitmentStrategiesNote?: string;
  relapsePrevention?: boolean;
  relapsePreventionNote?: string;

  // Progress (single select) + evidence free text
  initialProgress?: string;
  initialProgressEvidence?: string;

  // Homework + educational material (ACT)
  homework?: string;
  educationalMaterialAssigned?: boolean;
};

type PsychotherapyFormValues = {
  // Interventions & Client Response
  interventionsUsed?: string;
  clientReponseToInterventins?: string;

  // Homework & Progress
  homeworkAssigned?: string;
  timeSpend?: string;
  documentationTime?: string;
  billable?: boolean;
  procedureCodes?: Code[];

  presentingConcerns?: string;
  sessionFocus?: string;

  cbtAutomaticThoughts?: string;
  cbtBehavioralExperiments?: string;
  cbtDistortions?: string;

  // CBT visit note (nested payload — see PsychotherapyCbtVisitNote)
  psychotherapyCbtVisitNote?: PsychotherapyCbtVisitNote;
  // DBT visit note (nested payload — see PsychotherapyDbtVisitNote)
  psychotherapyDbtVisitNote?: PsychotherapyDbtVisitNote;
  // ACT visit note (nested payload — see PsychotherapyActVisitNote)
  psychotherapyActVisitNote?: PsychotherapyActVisitNote;
  dbtStrategies?: string;
  dbtMindfulness?: string;
  dbtDistressTolerance?: string;
  dbtEmotionRegulation?: string;
  dbtInterpersonal?: string;
  acceptanceWork?: string;
  cognitiveDefusion?: string;
  presentMomentAwareness?: string;
  selfAsContext?: string;
  valueClarification?: string;
  commitedAction?: string;
};
type PsychoeducationTherapyFormValues = {
  sessionRequired?: boolean;
  primaryTopic?: string;
  learningObjectives?: string;
  contentDelivered?: string;
  skillsCategoriesCovered?: string;
  skillsDescription?: string;
  materialsDistributed?: string;
  materialDetails?: string;
  engagementLevel?: string;
  comprehensionLevel?: string;
  questionsDiscussion?: string;
  participantFeedback?: string;
  inSessionPractice?: string;
  homePracticeAssignments?: string;
  realLifeApplicationPlan?: string;
  followUpTopics?: string;
  additionalResourcesNeeded?: string;
  nextSessionPlan?: string;
};
type CollaborativeCareModelFormValues = {
  primaryDiagnosis?: string;
  secondaryDiagnoses?: string;
  targetSymptom?: string;
  measurementTool?: string;
  treatmentGoal?: string;
  currentProgress?: string;
  currentPsychiatricMedications?: string;
  medicationChangesThisVisit?: string;
  sideEffectsReported?: string;
  adherenceIssuesOrBarriers?: string;
  behavioralActivation?: string;
  psychoeducationProvided?: string;
  problemSolving?: string;
  relapsePrevention?: string;
  pcpCommunication?: string;
  specialtyReferrals?: string;
  communityResources?: string;
  familyCaregiverInvolvement?: string;
  suicidalIdeation?: string;
  homicidalIdeation?: string;
  overallRiskLevel?: string;
  safetyPlanStatus?: string;
  treatmentPlanChanges?: string;
  patientResponseToTreatment?: string;
  barriersToTreatment?: string;
  nextStepsAndActionItems?: string;
  psychiatricConsultationNeeded?: boolean;
  reasonForConsultation?: string;
  urgencyLevel?: string;
};
type IndividualTherapyFormValues = {
  presentingConcernsToday?: string;
  sessionType?: string;
  patientEngagement?: number;
  appearance?: string;
  guardianParentName?: string;
  relationship?: string;
  mobileNumber?: string;
  selectedInterventions?: string;
  sessionFocusContent?: string;
  patientResponseToTreatment?: string;
  clinicalImpressions?: string;
  sessionFrequency?: string;
  coordinationNeeded?: string;
  nextAppointmentDate?: string;
  nextAppointmentTime?: string; // this time filed is for frontend only. we are merging date and time on save..
  treatmentGoalsProgress?: string;
  // need to discuss below fields.
  homeworkTasks?: HomeworkTask[];
};
export interface Form2Payload
  extends
    TreatMentPlanNote,
    PreOperativeNote,
    PsychiatricProgressExam,
    InitialPsychotherapy,
    InitialPsychiatricEvolution,
    PsychodynamicTherapyFormValues,
    PsychotherapyFormValues,
    IndividualTherapyFormValues,
    PsychoeducationTherapyFormValues,
    CollaborativeCareModelFormValues,
    TraumaFocusedTherapyFormValues,
    SolutionFocusedTherapyFormValues {
  allergyFreeText?: string;
  encounterStatus?: EncounterSumaryEncounterStatusEnum;
  subjectiveFreeNote?: string;
  objectiveFreeNote?: string;
  formType?: EncounterSumaryFormTypeEnum;
  procedureCodes?: Code[];
  providerName?: string;
  providerId?: string;
  providerNote?: string;
  psychoeducationProviderNote?: string;
  assessmentCodes?: Code[];
  assessment?: any;
  followUp?: string;
  carePlan?: string;
  appointmentId?: string;
  chiefComplaint?: string;
  noteContent?: Record<string, object>;
  /** Presigned URL of the Mini-Cog clock drawing saved in noteContent. */
  clockDrawingUrl?: string;
  problems: string;
  habits: string;
  hpi?: string;
  note?: string;
  tx?: string;
  patientVitals: PatientVital[];
  instruction: string;
  assessmentFreeNote?: string;
  planFreeNote?: any;
  // ── DAP note ──
  // Data section free text. The MSE, Risk Assessment, ICD/CPT and Plan keys are
  // shared with the Psychotherapy Note, so only these three are DAP specific.
  dataFreeNote?: string;
  // Comma-separated option names, e.g. "supportiveTherapy,dbtSkills".
  interventionsProvided?: string;
  interventionsProvidedNote?: string;
  otherTherapeuticInterventionsNote?: string;
  patientResponse?: string;
  patientResponseNote?: string;
  // ── BIRP note ──
  // One free text per section. MSE, Risk Assessment and the Plan keys are
  // shared with the Psychotherapy Note, so only these are BIRP specific.
  behaviorFreeNote?: string;
  interventionFreeNote?: string;
  responseFreeNote?: string;
  // Comma-separated option names, e.g. "activeListening,goalSetting".
  therapeuticInterventions?: string;
  therapeuticInterventionsNote?: string;
  topicsAddressed?: string;
  topicsAddressedNote?: string;
  patientParticipation?: string;
  patientParticipationNote?: string;
  responseToIntervention?: string;
  responseToInterventionNote?: string;
  // Single select — holds one option name, not a comma-separated string.
  progress?: string;
  // ── Progress note ──
  // Primary Diagnosis. The codes live in the shared `assessmentCodes` key and
  // use the shared picker, so only the justification is Progress Note specific.
  // The per-code primary marker was dropped along with the reorder UI, so the
  // note no longer writes this key.
  // primaryDiagnosisCode?: string;
  diagnosisJustification?: string;
  // Current Mental Status Examination. One option name per component, unlike
  // the comma-separated MSE keys the other note types write.
  mseOrientation?: string;
  mseGeneralAppearance?: string;
  mseDress?: string;
  mseMotorActivity?: string;
  mseInterviewBehavior?: string;
  mseSpeech?: string;
  mseMood?: string;
  mseAffect?: string;
  mseInsight?: string;
  mseJudgmentImpulseControl?: string;
  mseMemory?: string;
  mseAttentionConcentration?: string;
  mseThoughtProcess?: string;
  mseThoughtContent?: string;
  msePerception?: string;
  mseFunctionalStatus?: string;
  mseOverallNote?: string;
  // Risk Assessment. Structured, so it does not share `riskLevel` /
  // `riskLevelNote` with the Psychotherapy Note modal.
  riskDeniesAllAreas?: boolean;
  riskLevelOfRisk?: string;
  riskNeedToAct?: string;
  riskPlanToAct?: string;
  riskMeansToAct?: string;
  riskFactors?: string;
  riskProtectiveFactors?: string;
  riskAdditionalDetails?: string;
  // Medications. The medications themselves are the patient's chart records
  // linked to the appointment, so only the flag and the comments live here.
  noCurrentMedications?: boolean;
  medicationComments?: string;
  // Comma-separated option names, e.g. "cbt,mindfulness".
  interventionsUsed?: string;
  interventionsUsedNote?: string;
  otherInterventionsNote?: string;
  assessmentAdditionalNotes?: string;
  // Single selects — each holds one option name.
  recommendation?: string;
  prescribedTreatmentFrequency?: string;
}

export const ENCOUNTER_INITIAL_VITALS = [
  {
    selected: false,
    name: "bloodPressure",
    label: "Blood Pressure",
    unit: "mmHg",
  },
  {
    selected: false,
    name: "bloodGlucose",
    label: "Blood Glucose",
    unit: "mg/dL",
  },
  {
    selected: false,
    name: "bodyTemperature",
    label: "Body Temperature",
    unit: "f",
  },
  {
    selected: false,
    name: "heartRate",
    label: "Heart Rate",
    unit: "BPM",
  },
  {
    selected: false,
    name: "respirationRate",
    label: "Respiration Rate",
    unit: "BPM",
  },
  {
    selected: false,
    name: "height",
    label: "Height",
    unit: "m",
  },
  {
    selected: false,
    name: "weight",
    label: "Weight",
    unit: "lbs",
  },
  {
    selected: false,
    name: "o2_saturation",
    label: "Oxygen Saturation (SpO2)",
    unit: "%",
  },
  {
    selected: false,
    name: "pulseRate",
    label: "Pulse Rate",
    unit: "BPM",
  },
  {
    selected: false,
    name: "bmi",
    label: "Body Mass Index",
    unit: "kg/m^2",
  },
  {
    selected: false,
    name: "respiratoryVolume",
    label: "Respiratory Volume",
    unit: "ml",
  },
  {
    selected: false,
    name: "perfusionIndex",
    label: "Perfusion Index",
    unit: "%",
  },
  {
    selected: false,
    name: "peakExpiratoryFlow",
    label: "Peak Expiratory Flow",
    unit: "l/min",
  },
  {
    selected: false,
    name: "forceExpiratoryVolume",
    label: "Forced Expiratory Volume",
    unit: "l",
  },
];

// Psychotherapy note fields, declared so they start (and reset) as empty values
// rather than `undefined`. An input whose value goes from a string to
// `undefined` flips from controlled to uncontrolled, and React then leaves the
// text the user typed on screen — which is why a note did not visually clear on
// a session-type change. Keeping them here means formik.resetForm() clears the
// note on its own.
const PSYCHOTHERAPY_NOTE_INITIAL_VALUE: Partial<Form2Payload> = {
  primaryModality: "",

  // Subjective
  subjectiveChiefComplaint: "",
  historyOfPresentIllness: "",
  presentingConcerns: "",
  sessionFocus: "",
  overallMseNote: "",

  // Assessment / Plan / Follow up
  assessmentFreeNote: "",
  assessmentCodes: [],
  procedureCodes: [],
  planFreeNote: "",
  followUp: "",
  timeSpend: "",
  documentationTime: "",
  billable: false,

  // Modality specific note blocks
  psychotherapyActVisitNote: {},
  psychotherapyCbtVisitNote: {},
  psychotherapyDbtVisitNote: {},

  // Mental status exam
  appearance: "",
  behavior: "",
  speech: "",
  mood: "",
  affect: "",
  affectRange: "",
  thoughtProcess: "",
  thoughtContent: "",
  cognition: "",
  insight: "",
  insightJudgment: "",

  // Psychodynamic
  materialBroughtToSession: "",
  freeAssociations: "",
  dreamContent: "",
  dreamAnalysis: "",
  transferenceObservations: "",
  counterTransferenceReflections: "",
  defenseMechanismObserved: "",
  unconsciousConflictsExplored: "",
  earlyChildhoodExperiences: "",
  relationalPatterns: "",
  interpretationsOffered: "",
  clientResponseToInterpretations: "",
  walkingThroughProcess: "",
  insightsDeveloped: "",
  therapeuticRelationship: "",
  progressTowardTreatmentGoal: "",
  clinicalImpression: "",

  // Trauma focused
  triggersIdentified: "",
  dissociativeAssessment: "",
  groundingTechniquesUsed: "",
  windowOfToleranceAssessment: "",
  emdrPhase: "",
  targetMemoryImage: "",
  negativeCognitionNc: "",
  positiveCognitionPc: "",
  vocValidityOfCognition: "",
  emotions: "",
  sudScale: "",
  bodySensationsLocations: "",
  desensitizationSetsProcessing: "",
  installationProcess: "",
  bodyScanResults: "",
  closure: "",
  traumaNarrativeWork: "",
  cognitiveProcessingOfTrauma: "",
  somaticBodyBasedInterventions: "",
  resourceInstallation: "",
  copingSkillsPracticed: "",
  stabilizationWork: "",
  safetyPlanning: "",
  progressTowardTraumaTreatmentGoals: "",

  // Solution focused
  preferredFutureGoals: "",
  miracleQuestionResponse: "",
  exceptionsIdentified: "",
  copingQuestions: "",
  whatsAlreadyWorking: "",
  scalingQuestionUsed: "",
  currentRating: "",
  previousSessionRating: "",
  indicatorsOfProgress: "",
  clientStrengthsAndResources: "",
  complimentsOffered: "",
  smallChangesNoticed: "",
  betweenSessionTasks: "",
  nextStepsActions: "",
  goalsSetForThisSession: "",
  progressTowardSessionGoals: "",
  clientResponseToSession: "",

  // Shared across the modality forms
  clinicalImpressions: "",
  planForNextSession: "",
  riskAssessment: "",
  suicideRisk: "",
  homicideRisk: "",
};

// DAP note fields, declared here for the same reason as the psychotherapy block
// above: they must start (and reset) as empty strings so the inputs stay
// controlled and formik.resetForm() actually clears the note.
const DAP_NOTE_INITIAL_VALUE: Partial<Form2Payload> = {
  dataFreeNote: "",
  interventionsProvided: "",
  interventionsProvidedNote: "",
  otherTherapeuticInterventionsNote: "",
  patientResponse: "",
  patientResponseNote: "",
};

// BIRP note fields — same reason as the two blocks above: they must start (and
// reset) as empty values so the inputs stay controlled and formik.resetForm()
// actually clears the note.
const BIRP_NOTE_INITIAL_VALUE: Partial<Form2Payload> = {
  behaviorFreeNote: "",
  interventionFreeNote: "",
  responseFreeNote: "",
  therapeuticInterventions: "",
  therapeuticInterventionsNote: "",
  topicsAddressed: "",
  topicsAddressedNote: "",
  patientParticipation: "",
  patientParticipationNote: "",
  responseToIntervention: "",
  responseToInterventionNote: "",
  progress: "",
};

// Progress note fields — same reason as the three blocks above: they must start
// (and reset) as empty values so the inputs stay controlled and
// formik.resetForm() actually clears the note.
const PROGRESS_NOTE_INITIAL_VALUE: Partial<Form2Payload> = {
  diagnosisJustification: "",

  // Mental status exam
  mseOrientation: "",
  mseGeneralAppearance: "",
  mseDress: "",
  mseMotorActivity: "",
  mseInterviewBehavior: "",
  mseSpeech: "",
  mseMood: "",
  mseAffect: "",
  mseInsight: "",
  mseJudgmentImpulseControl: "",
  mseMemory: "",
  mseAttentionConcentration: "",
  mseThoughtProcess: "",
  mseThoughtContent: "",
  msePerception: "",
  mseFunctionalStatus: "",
  mseOverallNote: "",

  // Risk assessment
  riskDeniesAllAreas: false,
  riskLevelOfRisk: "",
  riskNeedToAct: "",
  riskPlanToAct: "",
  riskMeansToAct: "",
  riskFactors: "",
  riskProtectiveFactors: "",
  riskAdditionalDetails: "",

  // Medications
  noCurrentMedications: false,
  medicationComments: "",

  // Interventions used
  interventionsUsed: "",
  interventionsUsedNote: "",
  otherInterventionsNote: "",

  // Assessment / recommendation
  assessmentAdditionalNotes: "",
  recommendation: "",
  prescribedTreatmentFrequency: "",
};

export const FORM2PAYLOAD_INITIAL_VALUE: Form2Payload = {
  encounterStatus: "INTAKE",
  formType: "SIMPLE_SOAP_NOTE",
  problems: "",
  habits: "",
  patientVitals: ENCOUNTER_INITIAL_VITALS,
  instruction: "",
  ...PSYCHOTHERAPY_NOTE_INITIAL_VALUE,
  ...DAP_NOTE_INITIAL_VALUE,
  ...BIRP_NOTE_INITIAL_VALUE,
  ...PROGRESS_NOTE_INITIAL_VALUE,
};

export type RosAndPe = {
  templateNameType?: string;
  templateType?: PhysicalROSTemplateTemplateTypeEnum;
  description?: string;
  isSelected: boolean;
};

export type CarePlanTemplate = {
  templateType: string;
  templateName: string;
  goals: string;
  icdCodes: any;
  intervention: string;
  preventive_schedule: string;
};
